import {
  AskRequest,
  AskRequestCommentVisibilities,
  AskRequestCommentVisibility,
  AskRequestCreateType,
  AskRequestCreateTypes,
  AskRequestStatus,
} from '@hone-automation/common';
import { UIReportTransaction } from 'types';

export const transformAskRequestVisibility = (visibility: AskRequestCreateType): AskRequestCommentVisibility => {
  switch (visibility) {
    case AskRequestCreateTypes.Internal:
      return AskRequestCommentVisibilities.Internal;
    case AskRequestCreateTypes.ForAccountant:
      return AskRequestCommentVisibilities.All;
    case AskRequestCreateTypes.ForCustomer:
      return AskRequestCommentVisibilities.External;
    default:
      return AskRequestCommentVisibilities.All;
  }
};

export const QueueLabelMap = {
  BLOCKERS: 'Blockers',
  FOR_ME: 'For Me',
  CUSTOMER: 'For Customer',
  FOR_CLIENTS: 'For Client',
  KITCHEN_SYNC: 'For KitchenSync',
  CLOSED: 'Closed',
} as const;

export type QueueLabels = (typeof QueueLabelMap)[keyof typeof QueueLabelMap];

export const getRequestHeaderStatus = (foundFlaggedTransactions: AskRequestStatus[]): AskRequestStatus | undefined => {
  if (Array.isArray(foundFlaggedTransactions) && foundFlaggedTransactions.length > 0) {
    const hasAnyOpen = foundFlaggedTransactions.some(item => item === 'Open');
    const hasAnyInProgress = foundFlaggedTransactions.some(item => item === 'In Progress');
    if (hasAnyOpen) {
      return 'Open';
    } else if (hasAnyInProgress) {
      return 'In Progress';
    }
    return 'Closed';
  }
};

export const getCurrentTransactions = (
  trxBeingFlagged: UIReportTransaction | UIReportTransaction[],
  flaggedTransactions: AskRequest[]
) => {
  if (!trxBeingFlagged || Array.isArray(trxBeingFlagged)) {
    return trxBeingFlagged;
  }

  // Check if this transaction has multiple flagged versions
  const foundTrxs = flaggedTransactions?.filter(transaction => {
    const targetId = (trxBeingFlagged as UIReportTransaction).flagged?.transaction?.id;
    return Boolean(transaction.transaction?.id && targetId && transaction.transaction.id === targetId);
  });

  if (foundTrxs && foundTrxs.length > 1) {
    return foundTrxs.map(found => ({ ...trxBeingFlagged, flagged: found }));
  }

  return trxBeingFlagged;
};

export const status_map = {
  open: 'Open',
  inProgress: 'In Progress',
  closed: 'Closed',
};

/**
 * Normalizes status to AskRequest format ('Open', 'In Progress', 'Closed')
 *
 * Handles both:
 * - FlaggedCellProps format: 'open', 'inProgress', 'closed'
 * - AskRequest format: 'Open', 'In Progress', 'Closed'
 *
 * @param status - Status string in either format
 * @returns Normalized status in AskRequest format
 */
export const normalizeStatus = (status: string | undefined): string | undefined => {
  if (status === undefined || status === null) return undefined;

  // If already in AskRequest format, return as-is
  if (['Open', 'In Progress', 'Closed'].includes(status)) {
    return status;
  }

  // Map from FlaggedCellProps format
  return status_map[status as keyof typeof status_map] || status;
};
